'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [permissions, setPerms] = useState([]);
  const [csrfToken, setToken] = useState('');
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get('/api/auth/status', { withCredentials: true });
      if (response.data.isAuthenticated) {
        setUser(response.data.user);
        setGroups(response.data.groups);
        setPerms(response.data.permissions);
        setToken(response.data.csrfToken);
      } else {
        setUser(null);
        setGroups([]);
        setPerms([]);
        setToken(response.data.csrfToken);
      }
    } catch {
      setUser(null);
      setGroups([]);
      setPerms([]);
      setToken('');
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const value = useMemo(
    () => ({
      isAuthenticated: !!user,
      user,
      groups,
      permissions,
      loading,
      csrfToken,
      refreshAuth,
    }),
    [user, groups, permissions, loading, csrfToken, refreshAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
